import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminPortalLayout from '@/modules/admin/components/AdminPortalLayout';
import { useToast } from '@/hooks/useToast';
import { usePaymentIssues } from '@/hooks/queries';

export default function AdminPaymentIssuesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast, toasts, hideToast } = useToast();
  
  const page = parseInt(searchParams.get('page') || '1', 10);
  
  const { data, isLoading: loading, refetch } = usePaymentIssues(page, 50);

  const users = data?.users || [];
  const pagination = data?.pagination || {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  };

  const handleRefresh = () => {
    refetch();
  };

  const handlePageChange = (newPage) => {
    navigate(`/admin-portal/payment-issues?page=${newPage}`);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getIssueIcon = (type) => {
    switch (type) {
      case 'no_stripe_customer':
      case 'no_payment_method':
        return '💳';
      case 'overdue_invoice':
      case 'subscription_past_due':
        return '⚠️';
      case 'failed_invoice':
      case 'subscription_unpaid':
        return '❌';
      case 'unpaid_invoice':
        return '⏳';
      case 'subscription_canceled':
      case 'subscription_missing':
        return '📋';
      default:
        return '🔔';
    }
  };

  if (loading) {
    return (
      <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-zapt-purple mb-4"></div>
            <p className="text-gray-600">Loading payment issues...</p>
          </div>
        </div>
      </AdminPortalLayout>
    );
  }

  return (
    <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-gray-900">
              💳 Payment Issues
            </h1>
            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
          <p className="text-gray-600">
            {pagination.total === 0 
              ? 'No payment issues found' 
              : `${pagination.total} user${pagination.total !== 1 ? 's' : ''} with payment issues`}
          </p>
        </div>

        {/* Users List */}
        {users.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-500 text-lg">
              ✅ All users have payment methods configured and no outstanding payment issues!
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {users.map((user) => (
              <div
                key={user.userId}
                className={`bg-white rounded-lg border-2 p-6 shadow-sm ${
                  user.issues.some(i => i.severity === 'high')
                    ? 'border-red-300'
                    : 'border-yellow-300'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-1">
                      {user.userEmail}
                    </h3>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Total Monthly Fee: ${parseFloat(user.totalMonthlyFee || '0').toFixed(2)}/month
                        <span className="text-xs text-gray-500 ml-2">
                          ({user.apps.length} app{user.apps.length !== 1 ? 's' : ''})
                        </span>
                      </p>
                      {user.latestInvoiceStatus && (
                        <p className="text-xs text-gray-500 mb-1">
                          Latest Invoice: <span className={`font-medium ${
                            user.latestInvoiceStatus === 'paid' ? 'text-green-600' :
                            user.latestInvoiceStatus === 'uncollectible' ? 'text-red-600' :
                            'text-yellow-600'
                          }`}>{user.latestInvoiceStatus}</span> - ${parseFloat(user.latestInvoiceAmount || '0').toFixed(2)}
                          {user.latestInvoicePeriod && (
                            <span className="ml-2">
                              ({new Date(user.latestInvoicePeriod.start).toLocaleDateString()} - {new Date(user.latestInvoicePeriod.end).toLocaleDateString()})
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* User's Apps */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Apps ({user.apps.length}):</h4>
                  <div className="space-y-2 mb-4">
                    {user.apps.map((app) => (
                      <div key={app.appId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">{app.appName}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            ${parseFloat(app.monthlyMaintenanceFee || '0').toFixed(2)}/month
                          </span>
                        </div>
                        <button
                          onClick={() => navigate(`/admin-portal/app/${app.appId}`)}
                          className="px-3 py-1 text-xs bg-zapt-purple text-white rounded hover:bg-zapt-purple/90 transition-colors"
                        >
                          View →
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Issues List */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Payment Issues:</h4>
                  <div className="space-y-2">
                    {user.issues.map((issue, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${getSeverityColor(issue.severity)}`}
                      >
                        <span className="text-lg">{getIssueIcon(issue.type)}</span>
                        <span className="text-sm font-medium">{issue.message}</span>
                        {issue.stripeInvoiceId && (
                          <a
                            href={`https://dashboard.stripe.com/invoices/${issue.stripeInvoiceId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-auto text-xs underline hover:no-underline"
                          >
                            View in Stripe
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrevPage}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    pagination.hasPrevPage
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                          pagination.page === pageNum
                            ? 'bg-zapt-purple text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    pagination.hasNextPage
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
    </AdminPortalLayout>
  );
}

